import {
    Button,
    Dialog,
    Flex,
    Image,
    pxToRem,
    useDeviceBrowserContext,
    useStyles,
    VideoPlayer,
} from '@archipro-design/aria';
import * as S from './DirectoryHeroPotm.style';
import { useInViewport, useScroll, useSize } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { CloseIcon } from '@fluentui/react-icons-northstar';
import { useIsWebmLegacyDevice } from '@modules/root/hook/use-is-webm-legacy-device';
import { useHydrated } from 'remix-utils';
import FilePlayer from 'react-player/file';
import { Link } from '@remix-run/react';
import { ChevronDown } from '@archipro-design/icons';
import { useLogImpression, useTracker } from '@archipro-website/tracker';

interface DirectoryHeroPotmProps {
    id: string;
    title: string;
    professionalID: string;
    shortDescription?: string;
    ctaText: string;
    ctaLink?: string;
    projectLink?: string;
    autoplay: boolean;
    teaserVideoWebm: string;
    teaserVideoMp4: string;
    videoCoverDesktop: string;
    videoCoverMobile?: string;
    fullVideoLink?: string;
    projectsCount?: number;
    projectsRef?: React.RefObject<HTMLDivElement>;
    mobileVideoWebm: string;
    mobileVideoMp4: string;
}

function isAbsoluteUrl(str: string) {
    if (!str) return false;
    try {
        new URL(str);
        return true;
    } catch {
        return false;
    }
}

function transformFloatText({
    scrollY,
    heroHeight,
    screenHeight,
    isLandscape = false,
}: {
    scrollY: number;
    heroHeight: number;
    screenHeight: number;
    isLandscape?: boolean;
}) {
    if (isLandscape) {
        return {
            opacity: !heroHeight ? 0 : 1 - scrollY / screenHeight,
        };
    }
    const heightDiff =
        heroHeight > screenHeight * 1.5 ? heroHeight - screenHeight : 0;
    const scrollMax = heightDiff + 30;
    return {
        transform: `translate(0, ${pxToRem(
            scrollY < scrollMax ? -scrollMax + scrollY : -20,
        )})`,
        opacity: !heroHeight
            ? 0
            : 1 - (scrollY < scrollMax ? scrollY : scrollMax) / scrollMax,
    };
}

function formatToThousandsDelimiter(
    number: number,
    roundedByThousand: boolean = false,
) {
    const scrollToProjectsCount = roundedByThousand
        ? Math.floor(number / 1000) * 1000
        : number;
    return new Intl.NumberFormat('en-US', {}).format(scrollToProjectsCount);
}

function getFloatText(count: number) {
    const itemCount = formatToThousandsDelimiter(count, true);
    return `Scroll to discover ${
        itemCount !== '0' ? ` over ${itemCount} Projects` : ' more Projects'
    }`;
}

const DirectoryHeroPotmMobile = ({
    title,
    autoplay,
    mobileVideoWebm,
    mobileVideoMp4,
    videoCoverMobile = '',
    fullVideoLink,
    projectLink,
    ctaText,
    ctaLink,
    shortDescription,
    projectsCount,
    projectsRef,
    professionalID,
    id,
}: DirectoryHeroPotmProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [playing, setPlaying] = useState(false);
    const [videoSources, setVideoSources] = useState<string[]>([]);
    const [projectModalLink, setProjectModalLink] = useState('');
    const hydrated = useHydrated();
    const position = useScroll();
    const heroSize = useSize(ref);
    const device = useDeviceBrowserContext();
    const screenSize = {
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    };

    const isLandscape = screenSize.width > screenSize.height;

    const { keyframe } = useStyles();
    const { css } = useStyles({
        autoplay,
        playing,
        upDownMotionKeyframes: keyframe(S.UpDownMotionKeyframes),
        isLandscape,
    });
    const [inViewport] = useInViewport(ref, {
        threshold: 0.2,
    });
    const isWebmLegacyDevice = useIsWebmLegacyDevice();

    const tracker = useTracker();
    useLogImpression({
        data: {
            professionalID: Number(professionalID),
            itemID: Number(id),
            type: 'Potm_Directory',
        },
        ref,
    });

    const trackClick = () => {
        tracker.log('PotmHeroClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: JSON.stringify({
                    libraryItemId: id,
                }),
            },
            ga4Data: {
                libraryItemId: id,
            },
        });
    };

    const trackPotmVideoClick = () => {
        tracker.log('PotmHeroVideoClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: JSON.stringify({
                    videoLink: fullVideoLink,
                    libraryItemId: id,
                }),
            },
            ga4Data: {
                libraryItemId: id,
            },
        });
    };

    const [openFullVideo, setOpenFullVideo] = useState(false);
    const [floatStyle, setFloatStyle] = useState<React.CSSProperties>({});
    useEffect(() => {
        setFloatStyle(
            transformFloatText({
                scrollY: position?.top || 0,
                heroHeight: heroSize?.height || 0,
                screenHeight: screenSize.height,
                isLandscape,
            }),
        );
    }, [heroSize?.height, position?.top, screenSize.height, isLandscape]);

    useEffect(() => {
        // webm on IOS safari does not work well on auto-play on simulators
        // and mobile devices (supported but not well-handled). If putting
        // mobileVideoWebm at the first index, it will download both MP4 and
        // WEBM and play the MP4 only
        if (device.isSafari) return setVideoSources([mobileVideoMp4]);
        setVideoSources(
            isWebmLegacyDevice
                ? [mobileVideoMp4, mobileVideoWebm]
                : [mobileVideoWebm, mobileVideoMp4],
        );
    }, [mobileVideoMp4, mobileVideoWebm, isWebmLegacyDevice, device.isSafari]);

    const indicationText = getFloatText(projectsCount || 0);
    const scrollToProjects = () => {
        if (projectsRef?.current) {
            projectsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };
    //server render only cover image
    const video = hydrated ? (
        <div className={css(S.PlayerMobile)}>
            <FilePlayer
                url={videoSources}
                playing={autoplay && inViewport}
                muted={true}
                loop={true}
                controls={false}
                playsinline={true}
                width={'100%'}
                height={'auto'}
                // apply on buffer end only if it is autoplay
                // to avoid the gray flash on Safari because
                // Safari does not show the poster
                onBufferEnd={() => {
                    if (autoplay) setPlaying(true);
                }}
                // apply on play if it is not auto-play
                // but we do not have the design for manuel play
                // it's just as a fallback
                onPlay={() => {
                    if (!autoplay) setPlaying(true);
                }}
                config={{
                    attributes: {
                        preload: 'auto',
                        poster: videoCoverMobile,
                    },
                }}
            />
        </div>
    ) : null;

    useEffect(() => {
        const projectLinkObj =
            projectLink && isAbsoluteUrl(projectLink)
                ? new URL(projectLink)
                : new URL(projectLink || '', 'https://archipro.co.nz');
        const projectModalSearchParams = projectLinkObj.searchParams;
        if (projectModalSearchParams) {
            projectModalSearchParams.set('modal-video', '1');
        }
        setProjectModalLink(
            projectLink && projectModalSearchParams
                ? `${projectLink}?${projectModalSearchParams}`
                : '',
        );
    }, [projectLink]);

    const watchNowButton = projectModalLink ? (
        <Button
            as={Link}
            to={projectModalLink}
            className={css(S.VideoButtonMobile)}
            color={'dark'}
            size={14}
            onTouchStart={trackPotmVideoClick}
        >
            Watch now
        </Button>
    ) : (
        fullVideoLink && (
            <Button
                className={css(S.VideoButtonMobile)}
                color={'dark'}
                size={14}
                onClick={() => {
                    setOpenFullVideo(true);
                }}
                onTouchStart={trackPotmVideoClick}
            >
                Watch now
            </Button>
        )
    );

    return (
        <div ref={ref} className={css(S.DirectoryHeroContainer)}>
            <div className={css(S.Frame)}>
                <div className={css(S.VideoAspectMobile)}>
                    <div className={css(S.BannerCentralContainer)}>{video}</div>
                    <Image
                        className={css(S.CoverMobile)}
                        src={videoCoverMobile}
                        height={960}
                        width={540}
                        layout={'fill'}
                        objectFit={'contain'}
                        objectPosition={'center'}
                        alt={title || ''}
                        style={{
                            opacity: playing ? 0 : 1,
                        }}
                    />
                    <div className={css(S.DirectoryHeroOverlayMobile)}>
                        <Flex
                            vAlign={'center'}
                            fill={true}
                            className={css(S.SpacerMobile)}
                        >
                            <div className={css(S.DirectoryHeroContentMobile)}>
                                <h5 className={css(S.titleMobile)}>{title}</h5>
                                {shortDescription && (
                                    <p className={css(S.DescriptionMobile)}>
                                        {shortDescription}
                                    </p>
                                )}
                                <div className={css(S.Actions)}>
                                    {watchNowButton}
                                    {ctaLink && (
                                        <Button
                                            as={Link}
                                            to={ctaLink}
                                            color={'white'}
                                            className={css(
                                                S.PrimaryButtonMobile,
                                            )}
                                            size={14}
                                            onClick={trackClick}
                                        >
                                            {ctaText}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Flex>
                        <div
                            className={css(S.DirectoryHeroViewMoreMobile)}
                            style={floatStyle}
                            onClick={scrollToProjects}
                            role="button"
                        >
                            <span className={css(S.DirectoryHeroViewMoreLabel)}>
                                {indicationText}
                            </span>
                            <div
                                className={css(
                                    S.DirectoryHeroViewMoreIconMobile,
                                )}
                            >
                                <ChevronDown />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {fullVideoLink && (
                <Dialog
                    className={css(S.FullVideoModal)}
                    styles={{ maxWidth: '100vw', maxHeight: '100vh' }}
                    variables={{
                        overlayBackground: 'rgba(30,30,30,.9)',
                        rootBackground: 'transparent',
                        rootWidth: '100%',
                        rootHeight: '100%',
                        border: '0',
                        contentTopBorderColor: 'transparent',
                        rootPadding: '0',
                        overflow: false,
                    }}
                    open={openFullVideo}
                    onCancel={() => setOpenFullVideo(false)}
                    backdrop={true}
                    headerAction={{
                        icon: (
                            <CloseIcon className={css(S.FullVideoModalClose)} />
                        ),
                        title: 'Close',
                        onClick: () => setOpenFullVideo(false),
                    }}
                    onClick={() => setOpenFullVideo(false)}
                    content={
                        <div className={css(S.VideoAspect)}>
                            <VideoPlayer
                                src={fullVideoLink}
                                playing={true}
                                controls={true}
                                playsInline={true}
                            />
                        </div>
                    }
                />
            )}
        </div>
    );
};
export default DirectoryHeroPotmMobile;
